import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X, Lock, Download, Trash2, Loader2 } from 'lucide-react';
import { MaskedInput } from '@/components/ui/masked-input';
import { Alert, AlertDescription } from '@/components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const EditClientModal = ({ client, onClose, onSuccess, isAdmin = false }) => {
  const [tipoPessoa, setTipoPessoa] = useState(client.tipo_pessoa);
  const [formData, setFormData] = useState({
    nome_completo: client.nome_completo || '',
    cpf: client.cpf || '',
    razao_social: client.razao_social || '',
    cnpj: client.cnpj || '',
    email: client.email || '',
    telefone: client.telefone || ''
  });
  const [newDocuments, setNewDocuments] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [loading, setLoading] = useState(false);

  // Fetch existing documents on mount
  useEffect(() => {
    fetchDocuments();
  }, [client.id]);

  const fetchDocuments = async () => {
    try {
      setLoadingDocs(true);
      const response = await axios.get(`${API}/documents/client/${client.id}`, getAuthHeaders());
      setExistingDocuments(response.data);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setNewDocuments([...newDocuments, ...acceptedFiles]);
  }, [newDocuments]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    multiple: true
  });

  const removeNewDocument = (index) => {
    setNewDocuments(newDocuments.filter((_, i) => i !== index));
  };

  const downloadDocument = (doc) => {
    // Create blob from base64 and download
    const byteCharacters = atob(doc.file_data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: doc.file_type });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Deseja excluir este documento?')) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, getAuthHeaders());
      toast.success('Documento excluído');
      fetchDocuments();
    } catch (error) {
      toast.error('Erro ao excluir documento');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        email: formData.email || null,
        telefone: formData.telefone || null
      };

      // Admin pode editar todos os campos, parceiro apenas opcionais
      if (isAdmin) {
        payload.tipo_pessoa = tipoPessoa;
        if (tipoPessoa === 'fisica') {
          payload.nome_completo = formData.nome_completo;
          payload.cpf = formData.cpf;
        } else {
          payload.razao_social = formData.razao_social;
          payload.cnpj = formData.cnpj;
        }
      }

      await axios.put(`${API}/clients/${client.id}`, payload, getAuthHeaders());
      
      // Upload novos documentos se houver
      for (const doc of newDocuments) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', doc);
        uploadFormData.append('client_id', client.id);
        
        await axios.post(`${API}/documents/upload`, uploadFormData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      toast.success('Cliente atualizado com sucesso!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="edit-client-modal">
        <DialogHeader>
          <DialogTitle data-testid="edit-client-modal-title">Editar Cliente</DialogTitle>
          <DialogDescription>
            {isAdmin 
              ? 'Atualize as informações do cliente' 
              : 'Atualize as informações opcionais do cliente'}
          </DialogDescription>
        </DialogHeader>

        {/* Aviso para parceiros */}
        {!isAdmin && (
          <Alert className="bg-amber-50 border-amber-200">
            <Lock className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              Como parceiro, você pode editar apenas os campos opcionais (email e telefone). 
              Os dados obrigatórios são bloqueados.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="edit-client-form">
          {/* Tipo de Pessoa - apenas Admin pode alterar */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              Tipo de Pessoa
              {!isAdmin && <Lock className="w-3 h-3 text-muted-foreground" />}
            </Label>
            <RadioGroup 
              value={tipoPessoa} 
              onValueChange={isAdmin ? setTipoPessoa : undefined} 
              className="flex gap-4"
              disabled={!isAdmin}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="fisica" id="edit-fisica" disabled={!isAdmin} />
                <Label 
                  htmlFor="edit-fisica" 
                  className={`cursor-pointer ${!isAdmin ? 'text-muted-foreground' : ''}`}
                >
                  Pessoa Física
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="juridica" id="edit-juridica" disabled={!isAdmin} />
                <Label 
                  htmlFor="edit-juridica" 
                  className={`cursor-pointer ${!isAdmin ? 'text-muted-foreground' : ''}`}
                >
                  Pessoa Jurídica
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Campos obrigatórios - bloqueados para parceiro */}
          {tipoPessoa === 'fisica' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="edit_nome_completo" className="flex items-center gap-2">
                  Nome Completo *
                  {!isAdmin && <Lock className="w-3 h-3 text-muted-foreground" />}
                </Label>
                <Input
                  id="edit_nome_completo"
                  value={formData.nome_completo}
                  onChange={(e) => isAdmin && setFormData({ ...formData, nome_completo: e.target.value })}
                  required={isAdmin}
                  disabled={!isAdmin}
                  className={`input-focus ${!isAdmin ? 'bg-muted cursor-not-allowed' : ''}`}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_cpf" className="flex items-center gap-2">
                  CPF *
                  {!isAdmin && <Lock className="w-3 h-3 text-muted-foreground" />}
                </Label>
                {isAdmin ? (
                  <MaskedInput
                    mask="cpf"
                    id="edit_cpf"
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                    required
                    data-testid="edit-cpf-input"
                    className="input-focus"
                  />
                ) : (
                  <Input
                    id="edit_cpf"
                    value={formData.cpf}
                    disabled
                    className="bg-muted cursor-not-allowed"
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="edit_razao_social" className="flex items-center gap-2">
                  Razão Social *
                  {!isAdmin && <Lock className="w-3 h-3 text-muted-foreground" />}
                </Label>
                <Input
                  id="edit_razao_social"
                  value={formData.razao_social}
                  onChange={(e) => isAdmin && setFormData({ ...formData, razao_social: e.target.value })}
                  required={isAdmin}
                  disabled={!isAdmin}
                  className={`input-focus ${!isAdmin ? 'bg-muted cursor-not-allowed' : ''}`}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_cnpj" className="flex items-center gap-2">
                  CNPJ *
                  {!isAdmin && <Lock className="w-3 h-3 text-muted-foreground" />}
                </Label>
                {isAdmin ? (
                  <MaskedInput
                    mask="cnpj"
                    id="edit_cnpj"
                    value={formData.cnpj}
                    onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                    required
                    data-testid="edit-cnpj-input"
                    className="input-focus"
                  />
                ) : (
                  <Input
                    id="edit_cnpj"
                    value={formData.cnpj}
                    disabled
                    className="bg-muted cursor-not-allowed"
                  />
                )}
              </div>
            </>
          )}

          {/* Campos opcionais - todos podem editar */}
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-3">Campos editáveis</p>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_email">Email (opcional)</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-focus"
                  data-testid="edit-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_telefone">Telefone (opcional)</Label>
                <Input
                  id="edit_telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  className="input-focus"
                  data-testid="edit-telefone-input"
                />
              </div>
            </div>
          </div>

          {/* Documentos Existentes */}
          <div className="space-y-2 pt-2 border-t">
            <Label className="flex items-center justify-between">
              <span>Documentos Anexados</span>
              {loadingDocs && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </Label>
            {!loadingDocs && existingDocuments.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">Nenhum documento anexado</p>
            ) : (
              <div className="space-y-2 max-h-[150px] overflow-y-auto">
                {existingDocuments.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between bg-muted/50 p-2 rounded border">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-sm truncate" title={doc.filename}>{doc.filename}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => downloadDocument(doc)}
                        className="h-7 w-7 text-primary hover:text-primary/80"
                        title="Baixar documento"
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteDocument(doc.id)}
                        className="h-7 w-7 text-destructive hover:text-destructive/80"
                        title="Excluir documento"
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upload de Novos Documentos */}
          <div className="space-y-2">
            <Label>Adicionar Novos Documentos</Label>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                {isDragActive ? 'Solte os arquivos aqui...' : 'Arraste arquivos ou clique para selecionar'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">PDF ou JPEG</p>
            </div>
            {newDocuments.length > 0 && (
              <div className="space-y-2 mt-2">
                {newDocuments.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between bg-green-50 dark:bg-green-950 p-2 rounded border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-green-600" />
                      <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                      <span className="text-xs text-green-600">(novo)</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeNewDocument(index)}
                      className="h-6 w-6"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 btn-transition"
              data-testid="edit-client-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 btn-transition"
              data-testid="edit-client-submit-button"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditClientModal;
